"use client";

import { useEffect, useState } from "react";
import Link from "@/components/i18n/link";
import { ArrowRight } from "lucide-react";
import { EventCard } from "@/components/EventCard";
import { fetchEvents } from "@/lib/api";
import type { CiudadVivaEvent } from "@/lib/events";
import { eventCategoriesForGenres } from "@/lib/movie-event-affinity";
import { useCountry } from "@/lib/country-store";
import { countryName } from "@/lib/countries";
import { reportError } from "@/lib/client-log";
import { useLocale, useMessages } from "@/lib/i18n/client";
import messages from "./title-events.i18n";

const LIMIT = 4;

// Fila "¿Prefieres salir?" del detalle de Viva Cine: eventos próximos del país
// elegido, primero los de categorías afines al título (película musical →
// conciertos) y, si no alcanzan, cualquier evento próximo.
// Se pide en el navegador, directo al backend, a propósito: la ficha es
// estática (ISR de 7 días), así que pedidos server-side dejarían los eventos
// fijos una semana y cada regeneración gastaría una function de Netlify.
export function TitleEvents({ genreIds }: { genreIds?: number[] }) {
  const { country, loaded } = useCountry();
  const lang = useLocale();
  const t = useMessages(messages);
  const [result, setResult] = useState<{ country: string; events: CiudadVivaEvent[] } | null>(null);
  const genresKey = (genreIds ?? []).join(",");

  useEffect(() => {
    if (!loaded) return;
    let cancelled = false;
    const categories = eventCategoriesForGenres(genresKey ? genresKey.split(",").map(Number) : []);
    const request = (category?: string) =>
      fetchEvents({ country, category, limit: LIMIT, lang }).then((res) => res.data);

    // Las afines y las generales en paralelo: el relleno ya está listo si
    // las afines no alcanzan a 4.
    Promise.all([...categories.map((c) => request(c)), request()])
      .then((lists) => {
        const seen = new Set<string>();
        const events = lists
          .flat()
          .filter((e) => !seen.has(e.slug) && seen.add(e.slug))
          .slice(0, LIMIT);
        if (!cancelled) setResult({ country, events });
      })
      .catch((err) => {
        // La fila es un extra: si falla, no se muestra (queda registrado).
        reportError("title-events", err, { detail: `country=${country}` });
        if (!cancelled) setResult({ country, events: [] });
      });
    return () => {
      cancelled = true;
    };
  }, [country, loaded, lang, genresKey]);

  if (!result || result.country !== country || result.events.length === 0) return null;

  return (
    <section className="mt-10">
      <div className="mb-3 flex items-end justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold tracking-tight">{t.title}</h2>
          <p className="text-sm text-muted-foreground">{t.subtitle(countryName(country, lang))}</p>
        </div>
        <Link
          href="/eventos"
          className="inline-flex shrink-0 items-center gap-1 text-sm font-medium text-primary hover:underline"
        >
          {t.seeMore}
          <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
      {/* En celular, carrusel horizontal: 4 cards apiladas eran ~2000 px de
          scroll al final de la ficha. Sale hasta el borde de la pantalla
          (compensa el padding de container-page). */}
      <div className="-mx-5 flex snap-x snap-mandatory gap-4 overflow-x-auto px-5 pb-2 sm:mx-0 sm:grid sm:grid-cols-2 sm:gap-5 sm:overflow-visible sm:px-0 sm:pb-0 lg:grid-cols-4">
        {result.events.map((e, i) => (
          <div key={e.slug} className="w-[80%] shrink-0 snap-start sm:w-auto">
            <EventCard event={e} position={i + 1} />
          </div>
        ))}
      </div>
    </section>
  );
}
